import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Typography,
  Paper,
  Box,
  Chip,
  Divider
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { DEFAULT_PROVIDER, getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import useStyles from "./whatsAppModalStyles";
import useWhatsAppModal from "./useWhatsAppModal";
import TabPanel from "./TabPanel";
import TransferTab from "./TransferTab";
import ProviderCredentialsSection from "./ProviderCredentialsSection";
import AutomationSection from "./AutomationSection";
import { buildWhatsAppPayload } from "./whatsAppModalHelpers";
import QueueSelect from "../QueueSelect";
// ✅ FASE 2: Importar modal de preview de cambios
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
// ✅ NUEVO: Importar componentes mejorados de selección
import PromptSelect from "../PromptSelect";
import IntegrationSelect from "../IntegrationSelect";
// ✅ NUEVO: Importar modal de advertencia de conexión desconectada
import ConnectionWarningModal from "../ConnectionWarningModal";

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});


const WhatsAppModal = ({ open, onClose, whatsAppId, onSave, defaultProvider = DEFAULT_PROVIDER }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    activeTab,
    companyId,
    connectionWarningData,
    connectionWarningOpen,
    flowBuilders,
    formikInitialValues,
    handleChangeIntegration,
    handleChangePrompt,
    handleChangeQueue,
    handleCheckboxChange,
    handleClose,
    handleConfirmConfigChange,
    handleSaveWhatsApp,
    handleTabChange,
    history,
    isLoading,
    loadingFlowBuilders,
    loadingWhatsapps,
    pendingWhatsAppData,
    previewData,
    previewModalOpen,
    selectedFlowBuilder,
    selectedIntegration,
    selectedPrompt,
    selectedQueueId,
    setActiveTab,
    setConnectionWarningData,
    setConnectionWarningOpen,
    setFlowBuilders,
    setIsLoading,
    setLoadingFlowBuilders,
    setLoadingWhatsapps,
    setPendingWhatsAppData,
    setPreviewData,
    setPreviewModalOpen,
    setSelectedFlowBuilder,
    setSelectedIntegration,
    setSelectedPrompt,
    setSelectedQueueId,
    setStableInitialValues,
    setTabStates,
    setTransferQueueId,
    setTransferWhatsappId,
    setWhatsApp,
    setWhatsapps,
    stableInitialValues,
    tabStates,
    transferQueueId,
    transferWhatsappId,
    user,
    whatsApp,
    whatsapps,
  } = useWhatsAppModal({ open, onClose, whatsAppId, onSave, defaultProvider });

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle>
          {whatsAppId
            ? `${i18n.t("whatsappModal.title.edit")} - ${getWhatsAppProviderLabel(whatsApp.provider || defaultProvider)}`
            : `${i18n.t("whatsappModal.title.add")} - ${getWhatsAppProviderLabel(defaultProvider)}`}
        </DialogTitle>
        
        <Formik
          key={whatsAppId || 'new'} // ✅ FORZAR RE-MOUNT solo cuando cambia el ID
          initialValues={formikInitialValues}
          enableReinitialize={true} // ✅ RE-INICIALIZAR cuando initialValues cambien (al cargar datos desde la API)
          validationSchema={SessionSchema}
          onSubmit={async (values, actions) => {
            // ✅ Validar campos requeridos manualmente antes de enviar
            // Esto evita el error "not focusable" al validar campos ocultos
            const errors = {};
            if (!values.name || values.name.trim().length === 0) {
              errors.name = 'Required';
            }
            
            if (Object.keys(errors).length > 0) {
              // Si hay errores, mostrar la pestaña que contiene el campo con error
              if (errors.name) {
                setActiveTab(0); // Ir a pestaña CONEXIÓN
              }
              actions.setErrors(errors);
              actions.setSubmitting(false);
              toast.error("Por favor completa los campos requeridos.");
              return;
            }
            
            
            setTimeout(() => {
              handleSaveWhatsApp(values); // ✅ PASAR VALORES DIRECTAMENTE
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => {
            // ✅ Calcular estados de automatización para deshabilitar campos
            const hasPromptInForm = !!(selectedPrompt && selectedPrompt !== null && selectedPrompt !== "");
            const hasIntegrationInForm = !!(selectedIntegration && selectedIntegration !== null && selectedIntegration !== "");
            const hasFlowBuilderInForm = !!(selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "");
            const hasQueueInForm = !!(selectedQueueId && selectedQueueId !== null && selectedQueueId !== "" && selectedQueueId !== 0);
            
            return (
              <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {/* ✅ PESTAÑAS DE NAVEGACIÓN */}
                <Paper square style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="Configuración de WhatsApp"
                  >
                    <Tab label="Conexión" />
                    {/* ✅ ELIMINADO: Tab "Chatbot Inicial" - Ya no se usa */}
                    <Tab label="Transferencias" />
                  </Tabs>
                </Paper>

                {/* ✅ PESTAÑA 1: CONEXIÓN (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={0} className={classes.tabPanel}>
                  {/* ✅ OPTIMIZADO: Header más compacto */}
                  <div className={classes.sectionHeader}>
                    <Typography variant="h6" color="primary" style={{ marginBottom: '4px' }}>
                      <span role="img" aria-label="conexión">🔌</span> Configuración
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                      Configuración básica de la conexión de WhatsApp
                    </Typography>
                  </div>

                  <div className={classes.tabContent}>
                    {/* ✅ OPTIMIZADO: Campos en Grid de 2 columnas */}
                    <Grid container spacing={2}>
                      <ProviderCredentialsSection classes={classes} values={values} />

                      <AutomationSection
                        classes={classes}
                        selectedQueueId={selectedQueueId}
                        handleChangeQueue={handleChangeQueue}
                        selectedPrompt={selectedPrompt}
                        handleChangePrompt={handleChangePrompt}
                        selectedIntegration={selectedIntegration}
                        handleChangeIntegration={handleChangeIntegration}
                        selectedFlowBuilder={selectedFlowBuilder}
                        setSelectedFlowBuilder={setSelectedFlowBuilder}
                        setSelectedPrompt={setSelectedPrompt}
                        setSelectedIntegration={setSelectedIntegration}
                        setSelectedQueueId={setSelectedQueueId}
                        loadingFlowBuilders={loadingFlowBuilders}
                        flowBuilders={flowBuilders}
                        hasPromptInForm={hasPromptInForm}
                        hasIntegrationInForm={hasIntegrationInForm}
                        hasFlowBuilderInForm={hasFlowBuilderInForm}
                        hasQueueInForm={hasQueueInForm}
                      />
                    </Grid>
                  </div>
                  
                  {/* ✅ BOTONES DENTRO DE LA PESTAÑA CONEXIÓN - MEJOR VISIBILIDAD */}
                  {activeTab === 0 && (
                    <Box 
                      style={{ 
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
                        backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(2),
                        marginTop: theme.spacing(2),
                      }}
                    >
                      <Button
                        onClick={handleClose}
                        color="secondary"
                        disabled={isSubmitting || isLoading}
                        variant="outlined"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                        }}
                      >
                        {i18n.t("whatsappModal.buttons.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || isLoading}
                        variant="contained"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                          boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                        }}
                      >
                        {whatsAppId
                          ? i18n.t("whatsappModal.buttons.okEdit")
                          : i18n.t("whatsappModal.buttons.okAdd")}
                        {((isSubmitting || isLoading)) && (
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: theme.spacing(1) }}
                          />
                        )}
                      </Button>
                    </Box>
                  )}
                </TabPanel>

                {/* ✅ ELIMINADO: PESTAÑA 2: CHATBOT INICIAL - Ya no se usa */}
                {/* Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial */}

                {/* ✅ PESTAÑA 2: TRANSFERENCIAS (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={1} className={classes.tabPanel}>
                  <TransferTab
                    classes={classes}
                    tabStates={tabStates}
                    handleCheckboxChange={handleCheckboxChange}
                    setFieldValue={setFieldValue}
                    transferQueueId={transferQueueId}
                    setTransferQueueId={setTransferQueueId}
                    transferWhatsappId={transferWhatsappId}
                    setTransferWhatsappId={setTransferWhatsappId}
                    loadingWhatsapps={loadingWhatsapps}
                    whatsapps={whatsapps}
                    theme={theme}
                  />
                </TabPanel>
              </DialogContent>

              {/* ✅ BOTONES PARA PESTAÑA TRANSFERENCIAS - Solo mostrar si NO está en pestaña CONEXIÓN */}
              {activeTab !== 0 && (
                <DialogActions className={classes.dialogActions}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting || isLoading}
                    variant="outlined"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                    }}
                  >
                    {i18n.t("whatsappModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isLoading}
                    variant="contained"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                      boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                    }}
                  >
                    {whatsAppId
                      ? i18n.t("whatsappModal.buttons.okEdit")
                      : i18n.t("whatsappModal.buttons.okAdd")}
                    {((isSubmitting || isLoading)) && (
                      <CircularProgress
                        size={20}
                        style={{ marginLeft: theme.spacing(1) }}
                      />
                    )}
                  </Button>
                </DialogActions>
              )}
              </Form>
            );
          }}
          </Formik>
      </Dialog>

      {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />

      {/* ✅ FASE 2: Modal de preview de cambios de configuración */}
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingWhatsAppData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="whatsapp"
      />
    </div>
  );
};

export default React.memo(WhatsAppModal);
